import React from 'react'
import { observer } from 'mobx-react-lite'

import Badge from '@mui/material/Badge'
import Box from '@mui/material/Box'
import ListItem, { ListItemOwnProps } from '@mui/material/ListItem'
import ListItemAvatar from '@mui/material/ListItemAvatar'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import { useTheme } from '@mui/material/styles'

import { InstitutionLogo, Text } from '@mxenabled/mxui'

import { ErrorStatus } from './ConnectionStatusIcons'

import { useAccountDisplayName, useAppConfig, useGlobalCopyStore } from '../../common'
import { Account, ConnectionStatus } from '../../core'
import { getAccountBalanceDetails } from '../utils/AccountFields'
import { formatCurrency } from '../../core/utils/NumberFormatting'

interface AccountListItemProps {
  account: Account
  onRowClick?: () => void
  listItemProps?: ListItemOwnProps
  shouldShowConnectionStatus?: boolean
}

const AccountListItem: React.FC<AccountListItemProps> = ({
  account,
  onRowClick,
  listItemProps,
  shouldShowConnectionStatus = false,
}) => {
  const theme = useTheme()
  const displayName = useAccountDisplayName(account)
  const { config } = useAppConfig()
  const { accounts: accountsCopy } = useGlobalCopyStore()
  const { firstSubtitle, firstValue, secondSubtitle, secondValue } = getAccountBalanceDetails(
    account,
    accountsCopy,
    config.display_available_balance_in_accounts,
  )

  let statusIcon = undefined
  if (
    account.connectionStatus === ConnectionStatus.Error ||
    account.connectionStatus === ConnectionStatus.RequiresMfa
  ) {
    statusIcon = <ErrorStatus size={16} />
  }

  const content = (
    <>
      <ListItemAvatar>
        <Badge
          badgeContent={shouldShowConnectionStatus ? statusIcon : undefined}
          sx={{
            '& .MuiBadge-badge': {
              right: -1,
            },
          }}
        >
          <InstitutionLogo alt="MX" institutionGuid={account.institution_guid || ''} />
        </Badge>
      </ListItemAvatar>

      <ListItemText
        disableTypography={true}
        primary={
          <Text
            bold={true}
            color={account.is_hidden ? theme.palette.text.secondary : undefined}
            variant="body1"
          >
            {`${displayName} ${account.account_number ? `*${account.account_number}` : ''}`}{' '}
            {account.is_hidden && !account.is_closed && accountsCopy.is_hidden}
            {account.is_closed && accountsCopy.is_closed}
          </Text>
        }
        secondary={
          <>
            <Box sx={{ alignItems: 'center', display: 'flex', justifyContent: 'space-between' }}>
              <Text variant="caption">{firstSubtitle}</Text>
              <Text bold={true} variant="body2">
                {formatCurrency(firstValue, '0,0.00')}
              </Text>
            </Box>
            <Box sx={{ alignItems: 'center', display: 'flex', justifyContent: 'space-between' }}>
              <Text variant="caption">{secondSubtitle}</Text>
              <Text ml="auto" variant="caption">
                {secondValue}
              </Text>
            </Box>
          </>
        }
      />
    </>
  )

  return (
    <ListItem
      {...listItemProps}
      sx={{
        backgroundColor: 'background.paper',
        color: account.is_closed ? 'grey.700' : '',
        ...listItemProps?.sx,
      }}
    >
      {onRowClick ? (
        <ListItemButton alignItems="flex-start" onClick={onRowClick} sx={{ px: 24 }}>
          {content}
        </ListItemButton>
      ) : (
        content
      )}
    </ListItem>
  )
}

export default observer(AccountListItem)
